import axios from 'axios';

// En producción usa rutas relativas (nginx hace proxy), en desarrollo usa localhost
const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

// Crear instancia de axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  getCurrentUser: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  logout: () => api.post('/auth/logout'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (userData) => api.post('/users', userData),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  delete: (id) => api.delete(`/users/${id}`),
  getStats: () => api.get('/users/stats'),
};

// Projects API
export const projectsAPI = {
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  getBySlug: (slug) => api.get(`/public/project/${slug}`),
  create: (projectData) => api.post('/projects', projectData),
  update: (id, projectData) => api.put(`/projects/${id}`, projectData),
  delete: (id) => api.delete(`/projects/${id}`),
  getStats: (id) => api.get(`/projects/${id}/stats`),

  // Fields
  addField: (projectId, fieldData) => api.post(`/projects/${projectId}/fields`, fieldData),
  updateField: (projectId, fieldId, fieldData) =>
    api.put(`/projects/${projectId}/fields/${fieldId}`, fieldData),
  deleteField: (projectId, fieldId) => api.delete(`/projects/${projectId}/fields/${fieldId}`),
};

// Attendees API
export const attendeesAPI = {
   register: (projectSlug, formData) => api.post(`/attendees/register/${projectSlug}`, formData),
   uploadFacialPhoto: (attendeeId, photo) => {
     const formData = new FormData();
     formData.append('photo', photo);
     return api.post(`/attendees/${attendeeId}/facial-photo`, formData, {
       headers: { 'Content-Type': 'multipart/form-data' },
     });
   },
   uploadFacialPhotoLiveness: (attendeeId, frames) => {
     // CRÍTICO: Forzar serialización JSON explícita para evitar BindingError
     // Convertir a string JSON primero, luego axios lo parsea automáticamente
     const payload = JSON.parse(JSON.stringify({ frames }));

     return api.post(`/attendees/${attendeeId}/facial-photo-liveness`, payload, {
       headers: {
         'Content-Type': 'application/json',
       },
       // Forzar que axios NO transforme el request automáticamente
       transformRequest: [(data) => JSON.stringify(data)]
     });
   },
   uploadPhoto: (attendeeId, photo) => {
     const formData = new FormData();
     formData.append('photo', photo);
     return api.post(`/attendees/${attendeeId}/photo`, formData, {
       headers: { 'Content-Type': 'multipart/form-data' },
     });
   },
   checkInByQR: (qrCode) => api.post('/attendees/check-in/qr', { qrCode }),
   checkInByFacial: (projectId, photo) => {
     const formData = new FormData();
     formData.append('photo', photo);
     return api.post(`/attendees/check-in/facial/${projectId}`, formData, {
       headers: { 'Content-Type': 'multipart/form-data' },
     });
   },
   getProjectAttendees: (projectId, params) =>
     api.get(`/attendees/project/${projectId}`, { params }),
   getAttendeeDetails: (id) => api.get(`/attendees/${id}`),
   getMosaicPhotos: (projectId, params) =>
     api.get(`/attendees/project/${projectId}/mosaic`, { params }),
   delete: (id) => api.delete(`/attendees/${id}`),
   deleteAllProjectAttendees: (projectId) => api.delete(`/attendees/project/${projectId}/all`),
   clearCheckIn: (id) => api.post(`/attendees/${id}/clear-checkin`),
   deletePhoto: (attendeeId) => api.delete(`/attendees/${attendeeId}/photo`),
   exportPDF: (projectId) =>
     api.get(`/attendees/project/${projectId}/export/pdf`, {
       responseType: 'blob'
     }),
   exportExcel: (projectId) =>
     api.get(`/attendees/project/${projectId}/export/excel`, {
       responseType: 'blob'
     }),
};

// Branding API
export const brandingAPI = {
  get: (projectId) => api.get(`/branding/${projectId}`),
  update: (projectId, brandingData) => api.put(`/branding/${projectId}`, brandingData),
  uploadHeader: (projectId, image) => {
    const formData = new FormData();
    formData.append('image', image);
    return api.post(`/branding/${projectId}/header`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadFooter: (projectId, image) => {
    const formData = new FormData();
    formData.append('image', image);
    return api.post(`/branding/${projectId}/footer`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteHeader: (projectId) => api.delete(`/branding/${projectId}/header`),
  deleteFooter: (projectId) => api.delete(`/branding/${projectId}/footer`),
};

// Voices API
export const voicesAPI = {
  getAvailable: () => api.get('/voices/available'),
  getProjectVoice: (projectId) => api.get(`/voices/${projectId}`),
  configure: (projectId, voiceData) => api.put(`/voices/${projectId}`, voiceData),
  test: (voiceId, testMessage) => api.post('/voices/test', { voiceId, testMessage }),
  generateWelcome: (attendeeId) => api.post(`/voices/welcome/${attendeeId}`),
  delete: (projectId) => api.delete(`/voices/${projectId}`),
};

// Messaging API
export const messagingAPI = {
  getConfig: (projectId) => api.get(`/messaging/${projectId}/config`),
  updateConfig: (projectId, configData) => api.put(`/messaging/${projectId}/config`, configData),
  uploadEmailHeader: (projectId, formData) => api.post(`/messaging/${projectId}/upload-email-header`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  uploadEmailSignature: (projectId, formData) => api.post(`/messaging/${projectId}/upload-email-signature`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  sendReminder: (projectId) => api.post(`/messaging/${projectId}/reminder`),
  getHistory: (projectId, params) => api.get(`/messaging/${projectId}/history`, { params }),
};

// PhotoBooth API
export const photoboothAPI = {
  getPhotos: (projectSlug, params) => api.get(`/photobooth/${projectSlug}/photos`, { params }),
  banPhoto: (projectSlug, fileName) => api.post(`/photobooth/${projectSlug}/delete-photo`, { fileName }),
  unbanPhoto: (projectSlug, fileName) => api.post(`/photobooth/${projectSlug}/unban-photo`, { fileName }),
  deletePhotoPermanently: (projectSlug, fileName) => api.post(`/photobooth/${projectSlug}/delete-photo-permanent`, { fileName }),
  getBannedPhotos: (projectSlug) => api.get(`/photobooth/${projectSlug}/banned`),
  getInfo: (projectSlug) => api.get(`/photobooth/${projectSlug}/info`),
  getPanelInfo: (projectSlug, token) => api.get(`/photobooth/${projectSlug}/panel/${token}`),
  getDisplayInfo: (projectSlug, token) => api.get(`/photobooth/${projectSlug}/tv/${token}`),
  uploadPhoto: (projectSlug, formData) => api.post(`/photobooth/${projectSlug}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Calls API (Retell AI + Twilio)
export const callsAPI = {
  // Configuración
  getConfig: (projectId) => api.get(`/calls/${projectId}/config`),
  updateConfig: (projectId, configData) => api.put(`/calls/${projectId}/config`, configData),
  getGlobalNumber: () => api.get('/calls/global-number'),
  getAvailableVoices: () => api.get('/calls/available-voices'),

  // Iniciar llamadas
  startCampaign: (projectId, attendeeIds = null) => api.post(`/calls/${projectId}/campaign`, { attendeeIds }),
  makeIndividualCall: (projectId, attendeeId) => api.post(`/calls/${projectId}/call/${attendeeId}`),

  // Historial
  getHistory: (projectId, params) => api.get(`/calls/${projectId}/history`, { params }),

  // Configuración de Twilio
  configureTwilioWebhook: () => api.post('/calls/configure-twilio-webhook'),

  // Configurar autenticación SIP (crítico para llamadas salientes)
  configureSIPAuth: () => api.post('/calls/configure-sip-auth'),

  // Diagnóstico de configuración
  getDiagnostic: () => api.get('/calls/diagnostic'),
};

// API pública (sin autenticación JWT) para photobooth
const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export { publicApi };

export const muralAPI = {
  getConfig: (slug) => api.get(`/mural/${slug}/config`),
  updateConfig: (slug, config) => api.put(`/mural/${slug}/config`, config),
  getPhotos: (slug) => api.get(`/mural/${slug}/photos`)
};

// Custom Forms API
export const customFormsAPI = {
  get: (projectId) => api.get(`/projects/${projectId}/custom-form`),
  create: (projectId, data) => api.post(`/projects/${projectId}/custom-form`, data),
  update: (projectId, data) => api.put(`/projects/${projectId}/custom-form`, data),
  delete: (projectId) => api.delete(`/projects/${projectId}/custom-form`),
};

// Project Documents API
export const projectDocumentsAPI = {
  get: (projectId, docType) => api.get(`/projects/${projectId}/documents/${docType}`),
  create: (projectId, data) => api.post(`/projects/${projectId}/documents`, data),
  update: (projectId, docType, data) => api.put(`/projects/${projectId}/documents/${docType}`, data),
  delete: (projectId, docType) => api.delete(`/projects/${projectId}/documents/${docType}`),
};

// Public API (no auth) for custom forms and documents
export const publicCustomFormsAPI = {
  getCustomForm: (slug) => publicApi.get(`/public/project/${slug}/custom-form`),
  getDocument: (slug, docType) => publicApi.get(`/public/project/${slug}/documents/${docType}`),
};

export default api;
